/**
 * BriefStep — the v3 landing screen, ported from wireframe-briefflow-v3.
 *
 * Two panes: a left "conversation with Tessa" rail (guided step cards +
 * composer) and a right map-artifact card with the real Mapbox map. Zones are
 * drawn manually via the real ZoneEditor (CREATE tool) and live on
 * editor.world; the geocoder recenters the real map. "Generate designs" is
 * wired to editor.designGenerator in a later chunk.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useEvent } from "src/hooks/useEvent";
import type { Editor } from "src/types/Editor";
import { ENV } from "src/constants/env";
import { ToolSelection } from "src/types/MapState";
import { ZoneType } from "src/types/Zone";
import { WMS_CATALOG } from "src/constants/wmsCatalog";
import { pointInBounds } from "src/types/stencil/wmsCatalog";
import { Icon, ACCENT } from "./Icon";
import { BriefForm } from "./BriefForm";
import { BriefMap } from "./BriefMap";
import { OverlayControls } from "./OverlayControls";
import { fetchPlaces, type Place } from "../MapSearch";
import {
  ZONE_TYPES,
  zoneStyle,
  mixTotal,
  type BriefSectionState,
  type BoardBrief,
  type BriefZone,
  type ZoneTypeId,
  type Overlay,
} from "./briefModel";

/** Map the real engine ZoneType enum ↔ the rail's display labels. */
const ZONE_TYPE_TO_LABEL: Record<ZoneType, ZoneTypeId> = {
  [ZoneType.buildable]: "Buildable",
  [ZoneType.exclusion]: "Exclusion",
  [ZoneType.parking]: "Parking",
  [ZoneType.access]: "Access",
  [ZoneType.green_zones]: "Green zone",
};
const LABEL_TO_ZONE_TYPE: Record<ZoneTypeId, ZoneType> = {
  Buildable: ZoneType.buildable,
  Exclusion: ZoneType.exclusion,
  Parking: ZoneType.parking,
  Access: ZoneType.access,
  "Green zone": ZoneType.green_zones,
};

/** A zone as the rail displays it (derived from the real editor.world zones). */
interface ZoneRow {
  id: string;
  type: ZoneTypeId;
  name: string;
  area: number;
}

const bfIcons = {
  search: (
    <g>
      <circle cx="11" cy="11" r="7" />
      <path d="M16 16l5 5" />
    </g>
  ),
  pin: (
    <g>
      <path d="M12 21s-7-6.1-7-11a7 7 0 0 1 14 0c0 4.9-7 11-7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </g>
  ),
  draw: <path d="M4 18l9-9 3 3-9 9H4v-3zM14 8l2-2 3 3-2 2" />,
  undo: <path d="M9 7L4 12l5 5M4 12h11a4 4 0 0 1 0 8h-3" />,
  trash: <path d="M5 7h14M9 7V5h6v2M7 7l1 13h8l1-13" />,
  check: <path d="M5 13l4 4 10-10" />,
  plus: <path d="M12 5v14M5 12h14" />,
  x: <path d="M6 6l12 12M18 6L6 18" />,
  layers: <path d="M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5" />,
};

type StepId = "loc" | "zones" | "massing" | "mix" | "constraints";
const ORDER: StepId[] = ["loc", "zones", "massing", "mix", "constraints"];

export interface BriefStepProps {
  editor: Editor;
  section: BriefSectionState;
  setBriefFor: (id: string, brief: BoardBrief) => void;
  setAddressFor: (id: string, address: string) => void;
  setZonesFor: (id: string, zones: BriefZone[]) => void;
  setOverlaysFor: (id: string, overlays: Overlay[]) => void;
  onContinue: () => void;
  isNew: boolean;
  generating: boolean;
  onSaveAsNew?: () => void;
  /** editing → the brief differs from the revision that was generated */
  pending?: boolean;
  onExit?: () => void;
  /** show a "back to Explore" control (project already has designs) */
  canBack?: boolean;
}

export const BriefStep = observer(function BriefStep({
  editor,
  section,
  setBriefFor,
  setAddressFor,
  onContinue,
  isNew,
  generating,
  onSaveAsNew,
  pending,
  onExit,
  canBack,
}: BriefStepProps) {
  // Zones come from the real editor.world (drawn via ZoneEditor on the map).
  const worldZones = editor.world?.zones ?? [];
  const zoneRows: ZoneRow[] = worldZones.map((z) => ({
    id: z.id,
    type: ZONE_TYPE_TO_LABEL[z.zoneType],
    name: z.label || ZONE_TYPE_TO_LABEL[z.zoneType],
    area: +z.siteAreaHectares.toFixed(2),
  }));
  const buildableCount = worldZones.filter((z) => z.zoneType === ZoneType.buildable).length;
  const total = mixTotal(section.brief.mix);
  const canGenerate = buildableCount >= 1 && total === 100;

  const [pendingType, setPendingType] = useState<ZoneTypeId>("Buildable");
  const [query, setQuery] = useState(section.address || "");
  const [picking, setPicking] = useState(false);
  const isDrawing = editor.mapState.selectedTool.value.id === ToolSelection.CREATE;
  const firstZone = worldZones.length === 0;
  const hasLoc = !!section.address;
  const hasBuildable = buildableCount > 0;
  const expandAll = !isNew; // editing → show the whole thing at once

  /* ---- overlay tool (lives on the map, not the stepper) ---- */
  const [ovOpen, setOvOpen] = useState(false);
  const [coachDismissed, setCoachDismissed] = useState(false);
  const ovPoint = editor.mapState.viewportCenter;
  const hasAreaOverlays = !!ovPoint && WMS_CATALOG.some((e) => pointInBounds(ovPoint, e.bounds));
  const showCoach = hasLoc && hasAreaOverlays && !coachDismissed && !ovOpen;

  /* ---- guided wizard progression ---- */
  const [doneSet, setDoneSet] = useState<Partial<Record<StepId, boolean>>>({});
  const reachable = (id: StepId) => ORDER.slice(0, ORDER.indexOf(id)).every((p) => doneSet[p]);
  const activeSec: StepId = ORDER.find((id) => reachable(id) && !doneSet[id]) || "constraints";
  const doneCount = ORDER.filter((id) => doneSet[id]).length;
  const [openOverride, setOpenOverride] = useState<Partial<Record<StepId, boolean>>>({});
  const isOpen = (id: StepId) =>
    expandAll ? true : id in openOverride ? !!openOverride[id] : id === activeSec;
  const toggleSec = (id: StepId) => setOpenOverride((o) => ({ ...o, [id]: !isOpen(id) }));
  const secStatus = (id: StepId): "locked" | "done" | "active" =>
    !reachable(id) ? "locked" : doneSet[id] ? "done" : "active";
  const confirmStep = (id: StepId) => {
    setDoneSet((d) => ({ ...d, [id]: true }));
    setOpenOverride({});
  };

  /* ---- address search (real Mapbox geocoder → recenter the real map) ---- */
  const q = query.trim();
  const placesQuery = useQuery({
    queryKey: ["places", q],
    staleTime: Infinity,
    enabled: picking && q.length >= 2,
    queryFn: async () => fetchPlaces(q, ENV.MAPBOX_KEY),
  });
  const suggestions = picking && q.length >= 2 ? (placesQuery.data ?? []).slice(0, 5) : [];
  const recenter = (place: Place) => {
    setAddressFor(section.id, place.placeName);
    setQuery(place.placeName);
    setPicking(false);
    if (place.bbox) editor.mapState.fitBounds(place.bbox);
    else editor.mapState.flyTo(place.center);
    if (!expandAll) confirmStep("loc");
  };

  /* ---- manual drawing → real ZoneEditor on the map ---- */
  const startDraw = (type: ZoneTypeId) => {
    setPendingType(type);
    editor.view.selection.clearZones();
    editor.mapState.selectedTool.update(ToolSelection.CREATE);
  };
  const cancelDraw = () => editor.mapState.selectedTool.update(ToolSelection.SELECT);
  // When a zone finishes drawing, stamp the chosen type and return to select.
  const onZoneCreated = useEvent((zoneId: string) => {
    const zone = editor.world?.zones.find((z) => z.id === zoneId);
    if (zone) zone.setZoneType(LABEL_TO_ZONE_TYPE[pendingType]);
    editor.mapState.selectedTool.update(ToolSelection.SELECT);
  });
  const removeZone = (id: string) => {
    if (editor.world) {
      editor.world.replaceZones(editor.world.zones.filter((z) => z.id !== id));
    }
  };

  /* ---- per-step prose / labels / summaries ---- */
  const stepProse: Record<StepId, string> = {
    loc: "Where's the site? Search an address or area to center the map.",
    zones:
      "Mark out the site — draw your buildable zone on the map by clicking each corner. Add other zones if you need them.",
    massing: "Set how the buildings sit on the site — count, height and spacing.",
    mix: "Set the unit mix. The split must total 100%.",
    constraints: "Set the planning limits, then generate your designs.",
  };
  const stepN: Record<StepId, number> = {
    loc: 1,
    zones: 2,
    massing: 3,
    mix: 4,
    constraints: 5,
  };
  const stepLabel: Record<StepId, string> = {
    loc: "Location",
    zones: "Site zones",
    massing: "Massing",
    mix: "Unit mix",
    constraints: "Constraints",
  };
  const stepSummary: Record<StepId, string> = {
    loc: section.address || "Hamburg",
    zones: zoneRows.length
      ? `${zoneRows.length} zone${zoneRows.length > 1 ? "s" : ""} · ${buildableCount} buildable`
      : "",
    massing: `${section.brief.floors} floors · ${section.brief.floorHeight}m`,
    mix: `${total}% allocated`,
    constraints: `${section.brief.minHeight}–${section.brief.maxHeight}m${section.brief.podium.on ? " · podium" : ""}`,
  };
  const revealedSteps = expandAll ? ORDER : ORDER.filter((id) => reachable(id));

  /* the interactive control for a given step */
  const stepControl = (id: StepId) => {
    if (id === "loc")
      return (
        <div className="relative">
          <div className="flex items-center gap-2.5 rounded-lg pl-3 pr-2.5 bg-white border border-[#e5e7eb] focus-within:border-[#7c5cff] transition-colors">
            <span className="shrink-0" style={{ color: "#9CA3AF" }}>
              <Icon d={bfIcons.search} size={14} />
            </span>
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPicking(true);
              }}
              placeholder="Search address or place…"
              className="grow min-w-0 py-2 text-[12px] bg-transparent appearance-none border-none outline-none shadow-none"
              style={{ color: "#1F2937", border: "none", outline: "none", boxShadow: "none" }}
            />
            {query && (
              <button
                onClick={() => {
                  setQuery("");
                  setPicking(false);
                }}
                className="shrink-0"
                style={{ color: "#9CA3AF" }}
              >
                <Icon d={bfIcons.x} size={13} />
              </button>
            )}
          </div>
          {suggestions.length > 0 && (
            <div className="mt-1 bg-white border border-[#e5e7eb] rounded-lg shadow-lg p-1 absolute left-0 right-0 z-30 ring-1 ring-black/5">
              {suggestions.map((s) => (
                <button
                  key={s.id ?? s.placeName}
                  onClick={() => recenter(s)}
                  className="w-full text-left px-2 py-1.5 rounded-md text-[11px] hover:bg-[#F3F4F6] flex items-center gap-1.5"
                  style={{ color: "#4B5563" }}
                >
                  <span className="shrink-0" style={{ color: "#9CA3AF" }}>
                    <Icon d={bfIcons.pin} size={13} />
                  </span>
                  <span className="truncate">{s.placeName}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    if (id === "zones")
      return (
        <ZonesControl
          zones={zoneRows}
          drawing={isDrawing}
          pendingType={pendingType}
          firstZone={firstZone}
          onStartDraw={startDraw}
          onCancel={cancelDraw}
          onRemoveZone={removeZone}
        />
      );
    return (
      <BriefForm only={id} brief={section.brief} setBrief={(b) => setBriefFor(section.id, b)} />
    );
  };

  /* composer action for the active step (guided mode) */
  const composer = (() => {
    switch (activeSec) {
      case "loc":
        return { label: hasLoc ? "Location set" : "Search a location to begin", enabled: false };
      case "zones":
        return {
          label: hasBuildable ? "Continue to massing" : "Draw a buildable zone first",
          enabled: hasBuildable,
          act: () => confirmStep("zones"),
        };
      case "massing":
        return { label: "Continue to unit mix", enabled: true, act: () => confirmStep("massing") };
      case "mix":
        return {
          label: total === 100 ? "Continue to constraints" : `Unit mix is ${total}% — needs 100%`,
          enabled: total === 100,
          act: () => confirmStep("mix"),
        };
      case "constraints":
        return {
          label: generating
            ? "Generating designs…"
            : canGenerate
              ? "Generate designs"
              : buildableCount === 0
                ? "Draw a buildable zone first"
                : "Unit mix must total 100%",
          enabled: canGenerate && !generating,
          act: onContinue,
        };
      default:
        return { label: "", enabled: false };
    }
  })();

  return (
    <div className="h-full flex">
      {/* ===== left: the brief, as a conversation with Tessa ===== */}
      <div
        className="shrink-0 border-r flex flex-col"
        style={{ width: 408, background: "#ffffff", borderColor: "#E5E7EB" }}
      >
        {/* header */}
        <div
          className="px-5 py-4 border-b flex items-center gap-2.5"
          style={{ borderColor: "#E5E7EB" }}
        >
          {(canBack || !isNew) && (
            <button
              onClick={onExit}
              title="Back to Explore"
              className="w-7 h-7 grid place-items-center rounded-lg shrink-0 text-[#6b7280] hover:bg-[#f3f4f6] -ml-1"
            >
              <Icon d={<path d="M15 6l-6 6 6 6" />} size={18} />
            </button>
          )}
          <span
            className="w-7 h-7 rounded-lg grid place-items-center shrink-0"
            style={{ background: ACCENT, color: "#fff" }}
          >
            <Icon
              d={
                <g>
                  <path d="M6 3h9l4 4v14H6z" />
                  <path d="M14 3v5h5" />
                  <path d="M9 13h7M9 16h5" />
                </g>
              }
              size={15}
            />
          </span>
          <div className="leading-tight min-w-0">
            <div className="text-[16px] font-semibold" style={{ color: "#1F2937" }}>
              {isNew ? "New brief" : `${section.label} · new revision`}
            </div>
            <div className="text-[11px]" style={{ color: "#9CA3AF" }}>
              {isNew
                ? "Set up your site and brief."
                : "A copy you can edit — zones included. Generating saves it as a new revision."}
            </div>
          </div>
          {isNew && (
            <span
              className="ml-auto text-[10px] tabular-nums shrink-0"
              style={{ color: "#9CA3AF" }}
            >
              {doneCount}/{ORDER.length}
            </span>
          )}
        </div>

        {/* conversation transcript */}
        <div className="grow overflow-y-auto px-4 py-5 space-y-5">
          {revealedSteps.map((id) => (
            <div key={id} className="space-y-2">
              {!expandAll && secStatus(id) === "active" && (
                <p className="text-[14px] leading-relaxed px-0.5" style={{ color: "#374151" }}>
                  {stepProse[id]}
                </p>
              )}
              <ChatCard
                n={stepN[id]}
                label={stepLabel[id]}
                status={secStatus(id)}
                summary={stepSummary[id]}
                open={isOpen(id)}
                onToggle={() => toggleSec(id)}
              >
                {stepControl(id)}
              </ChatCard>
            </div>
          ))}
        </div>

        {/* composer */}
        <div className="px-4 pb-4 pt-3 border-t" style={{ borderColor: "#E5E7EB" }}>
          {expandAll ? (
            <div className="space-y-2">
              <button
                onClick={onSaveAsNew}
                disabled={!pending}
                className={
                  "w-full inline-flex items-center justify-center h-10 rounded-lg text-[12px] font-semibold " +
                  (pending ? "text-white" : "bg-[#f3f4f6] text-[#d1d5db] cursor-not-allowed")
                }
                style={pending ? { background: ACCENT } : undefined}
              >
                Save as new revision
              </button>
              <p className="text-[10px] text-center leading-snug" style={{ color: "#9CA3AF" }}>
                {pending
                  ? "Saves your changes as a new revision — the original stays unchanged."
                  : "Adjust anything above to save it as a new revision."}
              </p>
            </div>
          ) : (
            <button
              onClick={() => composer.enabled && "act" in composer && composer.act?.()}
              disabled={!composer.enabled}
              className={
                "w-full inline-flex items-center justify-center gap-1.5 h-11 rounded-lg text-[13px] font-semibold transition-colors " +
                (generating
                  ? "text-white cursor-wait"
                  : composer.enabled
                    ? "text-white"
                    : "bg-[#f3f4f6] text-[#d1d5db] cursor-not-allowed")
              }
              style={
                generating
                  ? { background: "rgba(124,92,255,0.5)" }
                  : composer.enabled
                    ? { background: ACCENT }
                    : undefined
              }
            >
              {composer.label}
              {!generating && composer.enabled && (
                <Icon d={<path d="M5 12h14M13 6l6 6-6 6" />} size={15} />
              )}
            </button>
          )}
        </div>
      </div>

      {/* ===== right: the real map, as an artifact panel ===== */}
      <div className="grow min-w-0 bg-[#f3f4f6] p-3">
        <div className="w-full h-full flex flex-col rounded-xl border border-[#e5e7eb] bg-white shadow-sm overflow-hidden">
          <div className="h-9 shrink-0 flex items-center gap-2 px-3 border-b border-[#e5e7eb]">
            <span className="w-2 h-2 rounded-full" style={{ background: ACCENT }} />
            <span className="text-[11px] font-medium text-[#4b5563]">Site map</span>
            <span className="text-[10px] text-[#9ca3af]">Mapbox</span>
            <div className="ml-auto relative">
              <button
                onClick={() => {
                  setOvOpen((v) => !v);
                  setCoachDismissed(true);
                }}
                className={
                  "inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] font-medium border transition-colors " +
                  (editor.mapState.stencil.loadedCatalogId
                    ? "text-[#7c5cff] border-[#c9bcff] bg-[rgba(124,92,255,0.06)]"
                    : "text-[#6b7280] border-[#e5e7eb] hover:bg-[#f9fafb]")
                }
              >
                <Icon d={bfIcons.layers} size={13} /> Overlays
              </button>
              {ovOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setOvOpen(false)} />
                  <div className="absolute right-0 top-9 z-20 w-[290px] rounded-lg border border-[#e5e7eb] bg-white shadow-lg p-2">
                    <OverlayControls mapState={editor.mapState} world={editor.world} />
                  </div>
                </>
              )}
              {showCoach && (
                <div className="absolute right-0 top-9 z-20 w-[228px] rounded-lg border border-[#c9bcff] bg-white shadow-lg p-3">
                  <div
                    className="absolute -top-1.5 right-7 w-3 h-3 rotate-45 border-l border-t border-[#c9bcff] bg-white"
                    aria-hidden
                  />
                  <div className="flex items-start gap-2">
                    <span className="mt-px shrink-0" style={{ color: ACCENT }}>
                      <Icon d={bfIcons.layers} size={15} />
                    </span>
                    <div>
                      <div className="text-[12px] font-semibold text-[#1f2937] mb-0.5">
                        Official site maps available
                      </div>
                      <p className="text-[11px] leading-snug text-[#6b7280]">
                        Overlay a BRW / land-value map for this area to guide where you draw your
                        zones.
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-1.5 mt-2.5">
                    <button
                      onClick={() => setCoachDismissed(true)}
                      className="h-7 px-2.5 rounded-md text-[11px] font-medium text-[#9ca3af] hover:text-[#4b5563]"
                    >
                      Dismiss
                    </button>
                    <button
                      onClick={() => {
                        setOvOpen(true);
                        setCoachDismissed(true);
                      }}
                      className="h-7 px-2.5 rounded-md text-[11px] font-semibold text-white"
                      style={{ background: ACCENT }}
                    >
                      Show overlays
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="grow relative overflow-hidden">
            <BriefMap editor={editor} onZoneCreated={onZoneCreated} />
            {isDrawing && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 text-[11px] text-[#4b5563] bg-white/95 border border-[#d1d5db] rounded-sm px-3 py-1.5 shadow-sm pointer-events-none">
                Drawing {pendingType} — click each corner on the map · click the first point to
                close · Esc to cancel
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

/* ---- zones step control (real ZoneEditor draw, tool-driven) ---- */
function ZonesControl({
  zones,
  drawing,
  pendingType,
  firstZone,
  onStartDraw,
  onCancel,
  onRemoveZone,
  readonly = false,
}: {
  zones: ZoneRow[];
  drawing: boolean;
  pendingType: ZoneTypeId;
  firstZone: boolean;
  onStartDraw: (t: ZoneTypeId) => void;
  onCancel: () => void;
  onRemoveZone: (id: string) => void;
  readonly?: boolean;
}) {
  return (
    <div>
      {readonly && (
        <p className="text-[10px] leading-snug mb-2 text-[#9ca3af]">
          Zones are fixed for this revision. Start a new brief to change the site.
        </p>
      )}
      {readonly ? null : drawing ? (
        <div
          className="rounded-lg border p-2.5"
          style={{ borderColor: "rgba(124,92,255,0.4)", background: "rgba(124,92,255,0.05)" }}
        >
          <div className="flex items-center gap-1.5 mb-2">
            <span className="text-[11px] font-semibold" style={{ color: ACCENT }}>
              Drawing: {pendingType}
            </span>
          </div>
          <p className="text-[10px] leading-snug mb-2" style={{ color: "#6B7280" }}>
            Click each corner on the map, then click the first point to close. Press Esc to cancel.
          </p>
          <button
            onClick={onCancel}
            className="w-full inline-flex items-center justify-center gap-1 py-1.5 rounded-md border text-[11px] text-[#4B5563] hover:bg-white"
            style={{ borderColor: "#E5E7EB" }}
          >
            <Icon d={bfIcons.x} size={13} /> Cancel drawing
          </button>
        </div>
      ) : firstZone ? (
        <button
          onClick={() => onStartDraw("Buildable")}
          className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12px] font-semibold"
          style={{
            border: "1px solid rgba(124,92,255,0.4)",
            background: "rgba(124,92,255,0.12)",
            color: ACCENT,
          }}
        >
          <Icon d={bfIcons.draw} size={14} /> Draw buildable zone
        </button>
      ) : null}

      {zones.length > 0 && (
        <div className="mt-2.5 space-y-1.5">
          {zones.map((z) => {
            const st = zoneStyle(z.type);
            const def = ZONE_TYPES.find((t) => t.id === z.type) ?? ZONE_TYPES[0];
            return (
              <div
                key={z.id}
                className="flex items-center gap-2 rounded-lg border px-2 py-1.5"
                style={{ borderColor: "#EAEAEA" }}
              >
                <span
                  className="w-5 h-5 shrink-0 grid place-items-center rounded-[3px] border text-[9px] font-bold"
                  style={{
                    borderColor: st.accent ? ACCENT : st.stroke,
                    color: st.accent ? ACCENT : "#9CA3AF",
                    background: st.accent ? "rgba(124,92,255,0.1)" : st.fill,
                  }}
                >
                  {def.abbr}
                </span>
                <div className="min-w-0 grow">
                  <div
                    className="text-[11px] truncate font-medium"
                    style={{ color: st.accent ? "#1F2937" : "#4B5563" }}
                  >
                    {z.name}
                  </div>
                  <div className="text-[9px] tabular-nums" style={{ color: "#9CA3AF" }}>
                    {z.type} · {z.area} ha
                  </div>
                </div>
                {!readonly && (
                  <button
                    onClick={() => onRemoveZone(z.id)}
                    title="Delete zone"
                    className="shrink-0"
                    style={{ color: "#D1D5DB" }}
                  >
                    <Icon d={bfIcons.trash} size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!readonly && !drawing && zones.length > 0 && (
        <div
          className="mt-2.5 rounded-lg border border-dashed p-2.5"
          style={{ borderColor: "#D1D5DB", background: "#ffffff" }}
        >
          <div
            className="text-[10px] font-semibold uppercase tracking-wide mb-1.5"
            style={{ color: "#9CA3AF" }}
          >
            Draw another zone
          </div>
          <div className="flex flex-wrap gap-1.5">
            {ZONE_TYPES.map((t) => (
              <button
                key={t.id}
                onClick={() => onStartDraw(t.id)}
                title={t.note}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] hover:bg-white"
                style={
                  t.id === "Buildable"
                    ? { borderColor: "rgba(124,92,255,0.5)", color: ACCENT }
                    : { borderColor: "#E5E7EB", color: "#4B5563" }
                }
              >
                <Icon d={bfIcons.plus} size={11} /> {t.id}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- chat-style card wrapping each step's control ---- */
function ChatCard({
  n,
  label,
  status,
  summary,
  open,
  onToggle,
  children,
}: {
  n: number;
  label: string;
  status: "locked" | "done" | "active";
  summary: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const locked = status === "locked";
  const done = status === "done";
  return (
    <div
      className="rounded-xl border"
      style={{ borderColor: open ? "#E5E7EB" : "#EAEAEA", background: "#fff" }}
    >
      <button
        type="button"
        disabled={locked}
        onClick={onToggle}
        className={
          "w-full flex items-center gap-2 px-3 py-2.5 text-left " +
          (locked ? "cursor-not-allowed" : "hover:bg-[#F9FAFB]")
        }
      >
        <span
          className="w-5 h-5 grid place-items-center rounded-full text-[10px] font-semibold shrink-0"
          style={
            done
              ? { background: ACCENT, color: "#fff" }
              : status === "active"
                ? { border: "1px solid " + ACCENT, color: ACCENT }
                : { border: "1px solid #D1D5DB", color: "#D1D5DB" }
          }
        >
          {done ? "✓" : n}
        </span>
        <span
          className="text-[12px] font-semibold shrink-0"
          style={{ color: locked ? "#D1D5DB" : "#374151" }}
        >
          {label}
        </span>
        {!open && !locked && summary && (
          <span className="text-[10px] truncate grow text-right" style={{ color: "#9CA3AF" }}>
            {summary}
          </span>
        )}
        {!locked && (
          <span
            className={"ml-auto shrink-0 transition-transform " + (open ? "rotate-180" : "")}
            style={{ color: "#9CA3AF" }}
          >
            <Icon d={<path d="M6 9l6 6 6-6" />} size={14} />
          </span>
        )}
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}
